import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";

/**
 * FCRA reject dialog shared across court bond retrieval pages.
 */
export function BondRetrievalRejectDialog({
  open,
  onClose,
  rejectFcraFile,
  setRejectFcraFile,
  rejectFcraUploading,
  setRejectFcraUploading,
  isRejectLoading,
  handleReject,
  uploadToADLS,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!rejectFcraUploading) {
          onClose();
        }
      }}
    >
      <DialogTitle>Reject Bond</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You may optionally attach an FCRA document before rejecting. You can also
          reject without uploading.
        </Typography>
        <Button
          variant="outlined"
          component="label"
          disabled={rejectFcraUploading}
          startIcon={<CloudUploadIcon />}
          sx={{
            borderRadius: 2,
            borderColor: "rgba(37, 99, 235, 0.5)",
            color: "#2563eb",
            fontWeight: 600,
            textTransform: "uppercase",
            mb: 1,
            "&:hover": {
              borderColor: "#2563eb",
              backgroundColor: "rgba(37, 99, 235, 0.04)",
            },
          }}
        >
          <span>Choose File</span>
          <input
            type="file"
            hidden
            accept="application/pdf,.pdf,image/*"
            onChange={(e) => setRejectFcraFile(e.target.files?.[0] || null)}
            disabled={rejectFcraUploading}
          />
        </Button>
        {rejectFcraFile && (
          <Typography
            variant="body2"
            sx={{ display: "block", mt: 1, color: "text.primary" }}
          >
            Selected File: {rejectFcraFile.name}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={rejectFcraUploading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => handleReject()}
          disabled={isRejectLoading}
        >
          Reject without FCRA
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={isRejectLoading || rejectFcraUploading}
          onClick={async () => {
            if (rejectFcraFile) {
              setRejectFcraUploading(true);
              try {
                const link = await uploadToADLS(rejectFcraFile);
                await handleReject({
                  fcraDocumentFileName: rejectFcraFile.name,
                  fcraDocumentLink: link,
                });
              } catch (err) {
                console.error(err);
                toast.error("Upload failed. Rejecting without FCRA document.");
                await handleReject();
              } finally {
                setRejectFcraUploading(false);
              }
            } else {
              await handleReject();
            }
          }}
          endIcon={
            rejectFcraUploading && <CircularProgress size={20} color="inherit" />
          }
        >
          {rejectFcraUploading ? "Uploading..." : "Upload & Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BondRetrievalRejectDialog;
